import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import {
	RiAddCircleLine,
	RiMapPinAddLine,
	RiWallet3Line,
	RiLockLine,
	RiArrowRightSLine,
	RiCheckLine,
	RiNotification3Line,
} from "@remixicon/react";
import Loader from "../common/Loader";
import StatusPill from "../common/StatusPill";
import { getLocations } from "../../api/locations";
import { getOrdersByType } from "../../api/bids";
import { getDwollaBalance } from "../../api/funds";
import { getNotifications } from "../../api/common";
import { orderNumber, formatGallons, formatMoney } from "../../utils/status";
import { CrudeCard } from "../Layout/BuyerSidebar";
import { useOilPrices } from "../../hooks/useMarketData";
import "./Dashboard.scss";

const ORDER_TABS = [
	{ key: "bidRequest", label: "Bid requests" },
	{ key: "openOrder", label: "Open orders" },
	{ key: "ordersInProgress", label: "In progress" },
];

const QUICK_ACTIONS = [
	{ to: "/bids/new", icon: RiAddCircleLine, title: "Request a bid", text: "Get competing fuel quotes from sellers", accent: true },
	{ to: "/closed-market", icon: RiLockLine, title: "Closed market", text: "Order at your negotiated daily price" },
	{ to: "/locations/new", icon: RiMapPinAddLine, title: "Add a location", text: "Set up a new delivery site" },
	{ to: "/funds", icon: RiWallet3Line, title: "Manage funds", text: "Banks, balance and transfers" },
];

export default function Dashboard() {
	const navigate = useNavigate();
	const user = useSelector((s) => s.auth.user);
	const { summary = {} } = useOutletContext() || {};
	const { wti, brent } = useOilPrices("daily");
	const [loading, setLoading] = useState(true);
	const [locationsCount, setLocationsCount] = useState(0);
	const [balance, setBalance] = useState(null);
	const [notifications, setNotifications] = useState([]);
	const [tab, setTab] = useState("bidRequest");
	const [orders, setOrders] = useState([]);
	const [ordersLoading, setOrdersLoading] = useState(true);

	useEffect(() => {
		(async () => {
			setLoading(true);
			const [locRes, balRes, notifRes] = await Promise.all([getLocations(), getDwollaBalance(), getNotifications()]);
			if (locRes && !locRes.error) setLocationsCount((locRes.data || []).length);
			if (balRes && !balRes.error) setBalance(balRes.data);
			if (notifRes?.notifications && !notifRes.notifications.error) setNotifications((notifRes.notifications.data || []).slice(0, 5));
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		(async () => {
			setOrdersLoading(true);
			const res = await getOrdersByType(tab);
			setOrders(res && !res.error ? res.data || [] : []);
			setOrdersLoading(false);
		})();
	}, [tab]);

	if (loading) return <Loader />;

	const balanceValue = typeof balance === "object" && balance !== null ? balance.value ?? balance.balance : balance;
	const verified = ["verified", "certified"].includes(String(user?.dwollaStatus || "").toLowerCase());
	const setupSteps = [
		{ done: locationsCount > 0, label: "Add your first delivery location", to: "/locations/new" },
		{ done: verified, label: "Verify your business", to: "/company-verification" },
		{ done: Number(balanceValue) > 0, label: "Link a bank and add funds", to: "/funds" },
	];
	const setupComplete = setupSteps.every((s) => s.done);

	return (
		<div>
			<div className="dash-hero">
				<div>
					<div className="text-muted small fw-semibold text-uppercase">{moment().format("dddd, MMMM D")}</div>
					<h4 className="page-title mt-1">
						{greeting()}
						{user?.firstName ? `, ${user.firstName}` : ""}
					</h4>
					<div className="page-subtitle">Here's what's happening with your fuel orders today.</div>
				</div>
				<div className="dash-balance">
					<span className="text-muted small fw-semibold">Wallet balance</span>
					<span className="dash-balance-value">{formatMoney(balanceValue || 0)}</span>
					<Link to="/funds" className="link-accent">
						Add funds
					</Link>
				</div>
			</div>

			<div className="quick-grid">
				{QUICK_ACTIONS.map((a) => (
					<Link key={a.to} to={a.to} className={`quick-card${a.accent ? " accent" : ""}`}>
						<span className="quick-icon">
							<a.icon size={20} />
						</span>
						<span className="quick-copy">
							<span className="quick-title">{a.title}</span>
							<span className="quick-text">{a.text}</span>
						</span>
						<RiArrowRightSLine size={18} className="quick-chev" />
					</Link>
				))}
			</div>

			{/* Phones have no sidebar, so surface its summary + crude prices here */}
			<div className="mobile-only">
				<div className="summary-strip">
					{[
						{ value: summary.bidRequests, label: "Active bids", to: "/orders", tab: "bidRequest" },
						{ value: summary.openOrders, label: "Open orders", to: "/orders", tab: "openOrder" },
						{ value: summary.inProgress, label: "In delivery", to: "/orders", tab: "ordersInProgress" },
						{ value: summary.unreadNotifications, label: "Unread alerts", to: "/notifications" },
					].map((c) => (
						<button type="button" key={c.label} className="summary-chip" onClick={() => navigate(c.to, c.tab ? { state: { tab: c.tab } } : undefined)}>
							<span className="summary-num">{summary.loading ? "–" : c.value ?? 0}</span>
							<span className="summary-label">{c.label}</span>
						</button>
					))}
				</div>
				<div className="mobile-market">
					<div className="d-flex justify-content-between align-items-center mb-2">
						<h6 className="section-title">Crude prices</h6>
						<Link to="/market-updates" className="link-accent">
							Market updates
						</Link>
					</div>
					<div className="crude-grid">
						<CrudeCard series={wti} placeholder="WTI Crude" />
						<CrudeCard series={brent} placeholder="Brent Crude" />
					</div>
				</div>
			</div>

			{!setupComplete && (
				<div className="card p-4 mb-4">
					<div className="d-flex justify-content-between align-items-center mb-2">
						<h6 className="section-title">Finish setting up your account</h6>
						<span className="text-muted small">
							{setupSteps.filter((s) => s.done).length} of {setupSteps.length} done
						</span>
					</div>
					<div className="setup-progress mb-3">
						<span style={{ width: `${(setupSteps.filter((s) => s.done).length / setupSteps.length) * 100}%` }} />
					</div>
					{setupSteps.map((s) => (
						<Link key={s.label} to={s.to} className={`setup-step${s.done ? " done" : ""}`}>
							<span className="setup-check">{s.done ? <RiCheckLine size={14} /> : null}</span>
							<span className="flex-grow-1">{s.label}</span>
							{!s.done && <RiArrowRightSLine size={18} />}
						</Link>
					))}
				</div>
			)}

			<div className="row g-4">
				<div className="col-xl-8">
					<div className="card">
						<div className="d-flex justify-content-between align-items-center px-4 pt-3">
							<h6 className="section-title">Your orders</h6>
							<Link to="/orders" state={{ tab }} className="link-accent">
								View all
							</Link>
						</div>
						<ul className="nav nav-tabs px-3 mt-2">
							{ORDER_TABS.map((t) => (
								<li className="nav-item" key={t.key}>
									<button className={`nav-link ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
										{t.label}
									</button>
								</li>
							))}
						</ul>
						{ordersLoading ? (
							<Loader small />
						) : orders.length === 0 ? (
							<div className="text-center py-5 px-3">
								<div className="fw-semibold mb-1">Nothing here yet</div>
								<div className="text-muted small mb-3">Request a bid and sellers will start competing for your order.</div>
								<Link to="/bids/new" className="btn-accent-solid">
									Request a bid
								</Link>
							</div>
						) : (
							<div className="table-responsive">
								<table className="table table-hover align-middle">
									<thead>
										<tr>
											<th className="ps-4">Order #</th>
											<th>Location</th>
											<th>Quantity</th>
											<th>Delivery</th>
											<th>Status</th>
										</tr>
									</thead>
									<tbody>
										{orders.slice(0, 6).map((o) => (
											<tr key={o._id || o.id} role="button" onClick={() => navigate(`/orders/${o.id || o._id}`)}>
												<td className="ps-4">
													<span className="order-link">{orderNumber(o)}</span>
												</td>
												<td>{o.locationDetails?.locationName || "-"}</td>
												<td>{formatGallons(o.orderSize)}</td>
												<td>{o.deliveryDate ? moment(o.deliveryDate).format("MMM D, YYYY") : "-"}</td>
												<td>
													<StatusPill order={o} />
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</div>
				<div className="col-xl-4">
					<div className="card p-4 h-100">
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h6 className="section-title">Recent notifications</h6>
							<Link to="/notifications" className="link-accent">
								View all
							</Link>
						</div>
						{notifications.length === 0 ? (
							<div className="text-muted small">You're all caught up.</div>
						) : (
							<ul className="list-unstyled m-0 notif-mini">
								{notifications.map((n, idx) => (
									<li key={n._id || idx}>
										<span className="notif-mini-icon">
											<RiNotification3Line size={15} />
										</span>
										<div className="min-w-0">
											<div className="fw-semibold small">{n.title || "Notification"}</div>
											<div className="text-muted small text-truncate">{n.description || n.message || n.body}</div>
											{n.createdAt ? <div className="text-muted" style={{ fontSize: 11 }}>{moment(n.createdAt).fromNow()}</div> : null}
										</div>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function greeting() {
	const h = new Date().getHours();
	if (h < 12) return "Good morning";
	if (h < 17) return "Good afternoon";
	return "Good evening";
}
